import React, { useEffect, useState } from "react";
import { StoryblokComponent, storyblokEditable } from "@storyblok/react";
import { ArticleModel } from "./ArticleModel";
import RelatedArticle from "../RelatedArticle/RelatedArticle";
import { ButtonModel } from "../../core/Models/ButtonModel";
import Button from "../../core/Button/Button";
import Hyperlink from "../../core/Hyperlink/Hyperlink";
import { HyperlinkModel } from "../../core/Models/HyperlinkModel";
import Utils from "../../core/Utils/Utils";
import { themeService } from "../../core/NotificationStripNav/ThemeService";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { ReactComponent as LinkSvg } from "../../../assets/images/link-svgrepo-com.svg";
import { useNotification } from "../../core/Notification/NotificationProvider";

import "./Article.css";

const Article: React.FC<{ blok: ArticleModel }> = ({ blok }) => {
  const { addNotification } = useNotification();
  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  const button: ButtonModel = {
    link: { cached_url: `category\\${blok.categories}`, linktype: "story" },
    label: blok.categories,
    borderColor: "#000000",
    color: "#FFFFFF",
    textColor: "#000000",
    hoverColor: "#FFFFFF",
  };
  const getHyperlink = (tag: string): HyperlinkModel => {
    return {
      label: tag,
      link: { cached_url: `tag\\${tag?.slice(1)}`, linktype: "story" },
    };
  };
  const href = window.location.href;
  const people = blok.people[0]?.content;
  const published_at = sessionStorage.getItem("published_at");
  const formattedDate = published_at
    ? Utils.getFormattedDate(published_at)
    : null;
  const handleIconClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigator.clipboard.writeText(window.location.href);
    addNotification("Link copied to clipboard!", "notification");
  };

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  return (
    <div
      {...storyblokEditable(blok)}
      className={`article ${blok.display}`}
      style={{ backgroundColor: theme?.panelColour, color: theme?.textColor }}
    >
      <div className="container">
        <div className="container-content">
          <div className="title">{blok.title}</div>

          <div className="categories-tags">
            <div className="category">
              {blok.categories && <Button blok={button}></Button>}
            </div>
            <div className="tags">
              {blok.tags
                ? blok.tags.map((tag) => (
                    <Hyperlink
                      blok={getHyperlink(tag)}
                      key={`tag-${tag}`}
                    ></Hyperlink>
                  ))
                : null}
            </div>
          </div>

          <div className="article-container-content">
            {blok.standfirst && (
              <div className="content-title">
                <span>{blok.standfirst}</span>
              </div>
            )}
            {blok.articleContent
              ? blok.articleContent.map((content) => (
                  <StoryblokComponent blok={content} key={content._uid} />
                ))
              : null}
          </div>
        </div>
      </div>

      <div className="related-articles-container">
        <div className="related-articles">
          <div className="related-title">RELATED ARTICLES</div>
          <div className="items">
            {blok.relatedArticles
              ? blok.relatedArticles.map((ra) => (
                  <RelatedArticle blok={ra} key={ra.uuid} />
                ))
              : null}
          </div>
        </div>
      </div>

      <div className="author-container">
        <div className="author-content">
          <div className="author">
            {people?.image?.filename && (
              <img
                src={Utils.getCacheUrl(people.image.filename)}
                alt=""
                onError={(e) =>
                  Utils.handleImageError(e, people.image.filename)
                }
              />
            )}
            <div className="author-body">
              <div className="author-name">
                By <span>{people.firstName + " " + people.lastName}</span>
              </div>
              <div className="author-published-date">{formattedDate}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="author-container">
        <div className="author-share-content">
          <div className="share-article">SHARE THIS ARTICLE</div>
          <div className="share-article-icons">
            <a
              className="icon-button-container"
              onClick={(e) => {
                handleIconClick(e);
              }}
              href={href}
            >
              <LinkSvg></LinkSvg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Article;
